import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import {
  AccreditationIcon,
  AddIcon,
  AddImageIcon,
  CancelIcon,
  ClassIcon,
  CorrectIcon,
  DeleteForeverIcon,
  EditIcon,
} from 'src/v3/core/icons'

import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectActiveLinkedService,
  fetchTemplates,
  selectTemplatesByLinkedServiceID,
  selectTemplatesStatus,
  selectActiveSection,
  fetchCourses,
  selectCoursesBySectionID,
  selectCoursesStatus,
  activeCourseChanged,
  selectActiveCourse,
  fetchAccreditations,
  selectAccreditations,
  selectAccreditationsStatus,
  fetchStoryboardSteps,
  selectStoryboardSteps,
  selectStoryboardStepsStatus,
  fetchClasses,
  selectClasses,
  selectClassesStatus,
} from 'src/v3/create/create.slice'
import { Course } from 'src/v3/core/types'
import Loading from 'src/v3/components/loading'

import { useFormik } from 'formik'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import { compose, F, T } from 'ramda'
import { processImageFile, uploadImageToS3 } from 'src/v3/utilities/s3'
import noop from 'src/utilities/noop'
import {
  SelectLinkedService,
  SelectSection,
} from 'src/v3/create/components/select-entities'
import ConfirmAction from 'src/v3/components/confirm-action'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let templates = useAppSelector(
    selectTemplatesByLinkedServiceID(activeLinkedService),
  )
  let templatesStatus = useAppSelector(selectTemplatesStatus)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let activeSection = useAppSelector(selectActiveSection)
  let courses = useAppSelector(selectCoursesBySectionID(activeSection))
  let activeCourse = useAppSelector(selectActiveCourse)
  let classes = useAppSelector(selectClasses)
  let classesStatus = useAppSelector(selectClassesStatus)
  let accreditations = useAppSelector(selectAccreditations)
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let storyboards = useAppSelector(selectStoryboardSteps)
  let storyboardStatus = useAppSelector(selectStoryboardStepsStatus)

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [uploading, setUploading] = useState<string>('')
  let [confirm, setConfirm] = useState<string>('')
  let [formOpen, setFormOpen] = useState<boolean>(false)

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      displayName: yup.string().required(),
      hours: yup.number().not([0]).required(),
      templateId: yup.string().not(['unselected']).required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
      displayName: '',
      hours: 0,
      templateId: 'unselected',
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post('/api/v2/create/course', {
            name: values.name,
            displayName: values.displayName,
            length: values.hours,
            templateId: values.templateId,
            sectionId: activeSection,
            linkedServiceId:
              activeLinkedService === 'unselected' ? null : activeLinkedService,
          })
          .then(() => dispatch(fetchCourses()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(
              err?.response?.data || err?.message || 'Error Creating Course',
            )
            dispatch(fetchCourses())
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/create/course', {
            id: values.id,
            name: values.name,
            displayName: values.displayName,
            length: values.hours,
            templateId: values.templateId,
          })
          .then(() => dispatch(fetchCourses()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(
              err?.response?.data || err?.message || 'Error Updating Course',
            )
            dispatch(fetchCourses())
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  useEffect(() => {
    if (templatesStatus === 'uninitialized') {
      dispatch(fetchTemplates())
    }
  }, [templatesStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (accreditationsStatus === 'uninitialized') {
      dispatch(fetchAccreditations())
    }
  }, [accreditationsStatus])

  useEffect(() => {
    if (storyboardStatus === 'uninitialized') {
      dispatch(fetchStoryboardSteps())
    }
  }, [storyboardStatus])

  useEffect(() => {
    if (!formOpen) {
      form.resetForm()
    }
  }, [formOpen])

  let onEdit = (id: string) => {
    let courseForEdit = courses.find((c) => c.id === id)
    form.setFieldValue('id', courseForEdit?.id)
    form.setFieldValue('name', courseForEdit?.name)
    form.setFieldValue('displayName', courseForEdit?.displayName)
    form.setFieldValue('hours', courseForEdit?.length)
    form.setFieldValue('templateId', courseForEdit?.templateID)
    setFormOpen(true)
  }

  let onImageUpload = (id: string, t: any) => {
    setUploading(id)
    return axios
      .put('/api/v2/create/course/image', { id })
      .then((response) => uploadImageToS3(t, response.data))
      .then(() => dispatch(fetchCourses()))
      .then(() => setUploading(''))
      .catch((err) => {
        setUploading('')
        setErrorMessage(err?.message || 'Error Uploading Image')
      })
  }

  let onPublish = (c: Course) => () => {
    let courseClasses = classes.filter((cl) => c.id === cl.courseID)
    let action = c.published === 'published' ? 'unpublish' : 'publish'

    if (action === 'publish') {
      if (
        !accreditations.some(
          (a) =>
            c.id ===
            (a.capce?.courseID ||
              a.ceBroker?.courseID ||
              a.virginia?.courseID ||
              a.notAccredited?.courseID),
        )
      ) {
        setErrorMessage('Courses require at least one accreditation')
        return
      }
      if (!courseClasses.length) {
        setErrorMessage('Courses require at least one class')
        return
      }
      if (
        !courseClasses.every((cl) =>
          storyboards.some((s) => s.classID === cl.id),
        )
      ) {
        setErrorMessage('Courses require that all classes have a storyboard')
        return
      }
    }

    setConfirm(`publish-${c.id}`)
  }

  return (
    <>
      <Box component="main" sx={{ p: 3 }}>
        <Typography
          variant="h4"
          className="my-4 d-inline"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Courses
        </Typography>
        <Typography
          className="ml-3 d-inline"
          sx={{
            marginBottom: '16px',
          }}
        >
          Create a course or select one to edit
        </Typography>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            margin: '16px 0',
          }}
        >
          <SelectLinkedService />
          <SelectSection />
        </div>
        <div>
          <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Box>
              <Paper
                sx={{
                  p: 2,
                  margin: 'auto',
                  flexGrow: 1,
                }}
              >
                <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  <Box className="flex gap-4">
                    <Box className="flex-1 flex flex-col gap-4">
                      <Box className="flex-1">
                        <Typography
                          gutterBottom
                          variant="subtitle1"
                          component="div"
                        >
                          <Skeleton animation={false} />
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <Skeleton animation={false} />
                        </Typography>
                      </Box>
                      <Box>
                        <div className="flex flex-row-reverse">
                          <Button
                            className="ml-3"
                            startIcon={<AddIcon />}
                            variant="contained"
                            disabled={activeSection === 'unselected'}
                            onClick={compose(setFormOpen, T)}
                          >
                            Add New Course
                          </Button>
                        </div>
                      </Box>
                    </Box>
                    <Box>{/* Nothing here */}</Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
            {courses.map((c) => {
              let action = c.published === 'published' ? 'unpublish' : 'publish'

              return (
                <Box>
                  <Paper
                    sx={{
                      p: 2,
                      margin: 'auto',
                      flexGrow: 1,
                    }}
                    style={{
                      backgroundImage: `url(${import.meta.env.VITE_CLOUDFRONT_URL}/public/${c.imageName})`,
                      backgroundSize: 'cover',
                      backgroundPosition: '0px 48%',
                      boxShadow: 'inset 0 0 0 1000px rgba(37, 43, 66, 0.94)',
                    }}
                  >
                    <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      <Box className="flex gap-4">
                        <Box className="flex-1 flex flex-col gap-4">
                          <Box className="flex flex-row">
                            <Box className="flex-1">
                              <Box sx={{ pl: 1 }}>
                                <Typography
                                  gutterBottom
                                  variant="subtitle1"
                                  component="div"
                                >
                                  {c.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  gutterBottom
                                >
                                  {c.displayName || 'N/A'}
                                </Typography>
                                <Chip
                                  sx={{ mt: 1 }}
                                  onClick={onPublish(c)}
                                  icon={
                                    c.published === 'published' ? (
                                      <CorrectIcon />
                                    ) : (
                                      <CancelIcon />
                                    )
                                  }
                                  label={
                                    c.published === 'published'
                                      ? 'Published'
                                      : 'Not Published'
                                  }
                                  variant={
                                    c.published === 'published'
                                      ? 'filled'
                                      : 'outlined'
                                  }
                                  color={
                                    c.published === 'published'
                                      ? 'success'
                                      : 'error'
                                  }
                                />
                                <ConfirmAction
                                  executeAction={() =>
                                    axios
                                      .put('/api/v2/create/publish-course', {
                                        id: c.id,
                                        newPublicationStatus: `${action}ed`,
                                      })
                                      .then(() => dispatch(fetchCourses()))
                                  }
                                  open={confirm}
                                  setOpen={setConfirm}
                                  openText={`publish-${c.id}`}
                                  title="Confirm Publish"
                                  text={`Are you sure you want to ${action} ${c.name}?`}
                                  onFailure={(err) =>
                                    setErrorMessage(
                                      err.message || 'Error Publishing Course',
                                    )
                                  }
                                />
                              </Box>
                            </Box>
                            <Box className="flex-1">
                              <Box
                                sx={{
                                  pl: 1,
                                  mt: 1,
                                  display: 'flex',
                                  flexDirection: 'row-reverse',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<AccreditationIcon />}
                                  onClick={() => {
                                    dispatch(activeCourseChanged(c.id))
                                    navigate('/create/accreditation')
                                  }}
                                >
                                  Manage Accreditations
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<ClassIcon />}
                                  onClick={() => {
                                    dispatch(activeCourseChanged(c.id))
                                    navigate('/create/class')
                                  }}
                                >
                                  Manage Classes
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                          <Box>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1,
                                flexDirection: 'row-reverse',
                                flexWrap: 'wrap-reverse',
                                alignItems: 'center',
                              }}
                            >
                              <Tooltip title="Delete">
                                <Button
                                  variant="outlined"
                                  onClick={() => setConfirm(`delete-${c.id}`)}
                                >
                                  <DeleteForeverIcon />
                                </Button>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <Button
                                  variant="outlined"
                                  onClick={() => onEdit(c.id)}
                                >
                                  <EditIcon />
                                </Button>
                              </Tooltip>
                              {uploading !== c.id ? (
                                <Tooltip title="Upload Thumbnail">
                                  <Button variant="outlined" component="label">
                                    <AddImageIcon onClick={noop} />
                                    <input
                                      type="file"
                                      accept=".jpg,.jpeg,.png"
                                      hidden
                                      onChange={(event) =>
                                        processImageFile(event, {
                                          onLoadingStart: noop,
                                          onLoadingEnd: (t: any) => {
                                            onImageUpload(c.id, t)
                                          },
                                        })
                                      }
                                    />
                                  </Button>
                                </Tooltip>
                              ) : (
                                <CircularProgress size={28} />
                              )}
                            </Box>
                          </Box>
                          <ConfirmAction
                            executeAction={() =>
                              axios
                                .delete(
                                  `/api/v2/create/delete-course?courseID=${c.id}`,
                                )
                                .then(() => dispatch(fetchCourses()))
                                .then(() => {
                                  if (activeCourse === c.id) {
                                    dispatch(activeCourseChanged('unselected'))
                                  }
                                })
                            }
                            open={confirm}
                            setOpen={setConfirm}
                            openText={`delete-${c.id}`}
                            title="Confirm Deletion"
                            text={`Are you sure you want to delete ${c.name}?`}
                            onFailure={(err) =>
                              setErrorMessage(
                                err.message || 'Error Deleting Course',
                              )
                            }
                          />
                        </Box>
                        <Box>{/* Nothing here */}</Box>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )
            })}
          </Box>
        </div>

        <Drawer
          anchor="bottom"
          open={formOpen && !saving}
          onClose={compose(setFormOpen, F)}
        >
          <Container maxWidth="sm">
            <Card className="m-4">
              <form className="p-4" onSubmit={form.handleSubmit}>
                <Tooltip title="Primary name of the course; used for accreditation">
                  <TextField
                    fullWidth
                    className="mt-4"
                    variant="standard"
                    value={form.values.name}
                    label="Name"
                    name="name"
                    onChange={form.handleChange}
                    error={form.touched.name && !!form.errors.name}
                  />
                </Tooltip>
                <Tooltip title="Alternate name for the course">
                  <TextField
                    fullWidth
                    className="mt-4"
                    variant="standard"
                    value={form.values.displayName}
                    label="Display Name"
                    name="displayName"
                    onChange={form.handleChange}
                    error={
                      form.touched.displayName && !!form.errors.displayName
                    }
                  />
                </Tooltip>
                <TextField
                  fullWidth
                  className="mt-4"
                  variant="standard"
                  value={form.values.hours}
                  label="Hours"
                  name="hours"
                  onChange={form.handleChange}
                  error={form.touched.hours && !!form.errors.hours}
                />
                <FormControl fullWidth className="mt-4">
                  <InputLabel sx={{ marginLeft: -2 }}>Template</InputLabel>
                  <Select
                    variant="standard"
                    value={form.values.templateId}
                    label="Template"
                    name="templateId"
                    onChange={form.handleChange}
                    error={form.touched.templateId && !!form.errors.templateId}
                  >
                    <MenuItem key="unselected" value="unselected">
                      Unselected
                    </MenuItem>
                    {templates.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <div className="flex flex-row-reverse">
                  <Button
                    variant="contained"
                    color="primary"
                    className="mt-4 mr-3"
                    type="submit"
                  >
                    Submit
                  </Button>
                </div>
              </form>
            </Card>
          </Container>
        </Drawer>
      </Box>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[
          accreditationsStatus,
          classesStatus,
          coursesStatus,
          storyboardStatus,
          templatesStatus,
        ]}
        loading={saving}
      />
    </>
  )
}
